import type { FeatureCollection } from "geojson";
import type { LayerInfo } from "src/common/managers/Types";
import { LayerType } from "src/common/managers/Types";
import type { LLMAnalysisResultItem } from "./ANNLLMAnalyzer";
import type { ANNServitutProvenance } from "./ANNServitutProvenance";
export enum AnnotationType {
   Stedfaestelse = "Stedfæstelse",
   Note = "Note",
   Document = "Document",
}
export enum AnnotationContextType {
   Servitut = "Servitut",
   Ejendom = "Ejendom",
}
export enum AnnotationQuality {
   Gold = "Gold",
   Silver = "Silver",
   Bronze = "Bronze",
}
export const ANNOTATION_TYPES = [
   "Stedfæstelse",
   "Dokument",
   "Rigsarkiv",
   "Transskription",
   "Påtaleberettiget",
   "Resumé",
   "Rådighed/tilstand",
   "Redegørelse",
   "Påtegning",
   "Notat"
] as const;
export type AnnotationTypeCategory = typeof ANNOTATION_TYPES[number];
export interface ANNImageOverlayData {
   coordinates: [number, number][];
   locked?: boolean;
   dataUrl?: string;
   s3Key?: string;
   s3Url?: string;
   fileName?: string;
}
export interface ANNLayerSnapshot {
   layerId?: number;
   datasetName?: string;
   filename?: string;
   type: LayerType;
   styling?: LayerInfo["styling"];
   visible?: boolean;
   hasNoDataBehind?: boolean;
   imageOverlay?: ANNImageOverlayData;
   geoJson?: FeatureCollection;
   propertiesInGeoJson?: LayerInfo["propertiesInGeoJson"];
   propertiesToDisplay?: LayerInfo["propertiesToDisplay"];
}
export interface ANNNotePayload {
   comment: string;
   text: string;
}
export interface ANNDocumentLlmAnalysis {
   analyzedAt: string;
   caseDescription?: string | null;
   resultsAccepted?: boolean;
   item: LLMAnalysisResultItem;
}

export interface ANNDocumentPayload {
   filename?: string;
   s3Key: string;
   s3Url?: string;
   mimeType?: string;
   size?: number;
   /** 1-based page numbers kept from the source akt PDF when saving servituttekst. */
   extractedPages?: number[];
   /** Structured AI analysis so redegørelse can hydrate without re-calling the AI backend. */
   llmAnalysis?: ANNDocumentLlmAnalysis;
   /** Source / AI / person-review marks for servitutredegørelse A+B. */
   provenance?: ANNServitutProvenance;
}
export interface ANNAnnotationHistoryEntry {
   timestamp: string;
   userId: string;
   userName: string;
   quality?: AnnotationQuality;
   verified?: boolean;
   comment?: string;
}
export interface ANNAnnotationRecord {
   id: string;
   documentId?: string;
   bfeNumber?: string | number;
   datoloebenr?: string;
   contextType: AnnotationContextType;
   organizationId: string;
   createdAt: string;
   createdBy: string;
   createdByName: string;
   title?: string;
   comment?: string;
   history?: ANNAnnotationHistoryEntry[];
   layers?: ANNLayerSnapshot[];
   note?: ANNNotePayload;
   document?: ANNDocumentPayload;
   type: AnnotationType;
   annotationType?: AnnotationTypeCategory;
   quality?: AnnotationQuality;
   verified?: boolean;
   lastEditedAt?: string;
   lastEditedBy?: string;
   lastEditedByName?: string;
   verifiedQuality?: AnnotationQuality;
   verifiedBy?: string;
   verifiedByName?: string;
   verifiedAt?: string;
}
export interface SaveAnnotationInput {
   documentId?: string;
   bfeNumber?: string | number;
   datoloebenr?: string;
   contextType: AnnotationContextType;
   organizationId: string;
   userId: string;
   userName: string;
   type: AnnotationType;
   layers?: ANNLayerSnapshot[];
   note?: ANNNotePayload;
   document?: ANNDocumentPayload;
   title?: string;
   comment?: string;
   historyComment?: string;
   id?: string;
   annotationType?: AnnotationTypeCategory;
   quality?: AnnotationQuality;
   verified?: boolean;
   originalCreatedAt?: string;
   originalCreatedBy?: string;
   originalCreatedByName?: string;
}
